import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./api";

/** Version of the build this page is running, injected by the Go server. */
const clientVersion: string | undefined = window.__KARTIS_CONFIG__?.version;

/** How often the page checks the server for a new deployed version. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;
/** How long the blocking overlay is shown before the page reloads itself. */
const RELOAD_DELAY_MS = 3_000;
/**
 * Minimum gap between two forced reloads of the same tab. Bounds reload
 * flapping during rolling deploys (old + new pods answering alternately)
 * to at most one reload per minute.
 */
const RELOAD_GUARD_MS = 60_000;
const RELOAD_GUARD_KEY = "kartis.forcedReloadAt";

interface LiveResponse {
  status: string;
  version: string;
}

/**
 * Detects that this tab is running an outdated frontend build and reloads
 * it: polls /api/live every 5 minutes (plus an explicit re-check whenever
 * the window regains focus, so returning to the tab checks immediately)
 * and compares the served version with the one this page was loaded with.
 *
 * Returns true when a refresh is pending — the caller must render a
 * blocking overlay so no further user action is possible; the page then
 * reloads itself automatically.
 *
 * Disabled in dev builds (no injected config / version "dev"), where
 * version comparisons are meaningless.
 */
export function useForcedRefresh(): boolean {
  const [refreshNeeded, setRefreshNeeded] = useState(false);
  const enabled = !!clientVersion && clientVersion !== "dev";

  const { data, refetch } = useQuery<LiveResponse>({
    queryKey: ["app-version"],
    queryFn: () => apiGet<LiveResponse>("/live"),
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: true,
    enabled,
  });

  // TanStack Query v5's focusManager only listens to visibilitychange
  // (tab switch, minimise) — returning from another OS window while the
  // tab stayed visible doesn't refetch, and staleTime suppresses checks
  // within 30 s. Refetch explicitly on window focus so coming back to
  // the app always verifies the version immediately.
  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => void refetch();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, refetch]);

  const serverVersion = data?.version;
  useEffect(() => {
    if (enabled && serverVersion && serverVersion !== clientVersion) {
      setRefreshNeeded(true);
    }
  }, [enabled, serverVersion]);

  useEffect(() => {
    if (!refreshNeeded) return;
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
    const sinceLast = Date.now() - lastReloadAt;
    const delay = Math.max(RELOAD_DELAY_MS, RELOAD_GUARD_MS - sinceLast);
    const timer = window.setTimeout(() => {
      sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
      window.location.reload();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [refreshNeeded]);

  return refreshNeeded;
}
